import { Component, Emit, Prop, Vue, Watch } from 'vue-property-decorator';
import Modal from '@/assets/common/Modal/Modal.vue'
import Paginate from 'vuejs-paginate'
import { toCamel } from "snake-camel";
import { Chemicals } from "@/types/Component";
import { ChemicalsResponse } from "@/types/APIResponse";

export type SelectedChemical = {
    id: number;
    name: string;
    disabledCheck?: boolean;
}

@Component({
    components: {
        Modal,
        Paginate
    }
})
export default class ChemicalSelectModal extends Vue {
    @Prop()
    private value!: boolean
    @Prop({ default: ()=>[] })
    private selectedChemical!: SelectedChemical[]

    @Prop()
    private isFromShippingItems!: boolean

    @Prop()
    private cropItemId!: string;

    private showAllChemicals = false;

    private title = '農薬選択'
    private currentPage = 1
    private totalPages = 0
    private searchType = 'name'
    private keyword = ''
    private offset = 0
    private limit = 20
    private isLoading = false
    private sortColumn: 'id' | 'display_type' | 'display_name' = 'id'
    private sortOrder: 'asc' | 'desc' = 'asc'
    private chemicals: Chemicals = {
        chemicals: [],
        hasMore: false,
        totalCount: 0
    }
    private selectedItems: SelectedChemical[] = []

    get showChemicalSelectModal(): boolean {
        return this.value
    }
    set showChemicalSelectModal(b: boolean) {
        this.value = b
    }

    private disabledCheck(id: number): boolean {
        const chemical = this.selectedChemical.find(sc => sc.id === id)
        return chemical?.disabledCheck !== undefined && chemical.disabledCheck
    }

    private resetSortState() {
        this.sortColumn = 'id'
        this.sortOrder = 'asc'
        this.currentPage = 1
        this.offset = 0
    }

    private init() {
        this.searchType = 'name'
        this.keyword = ''
        this.resetSortState()
        this.selectedItems = []
        this.refreshChemicalList().then(() => {
            // propsで受けた農薬を選択状態にする
            if(this.selectedChemical?.length > 0) {
                this.selectedItems = Object.assign([], this.selectedChemical.map(sc => ({id: sc.id, name: sc.name})))
            }
        })
    }

    created(): void {
        this.init()
    }

    @Watch('value')
    openedModal(): void {
        if(this.value) {
            this.init()
        }
    }

    @Emit('save')
    save(): SelectedChemical[] {
        // 選択済みの農薬を登録番号の昇順で返す
        this.showAllChemicals= false;
        return this.selectedItems.sort((a, b) => a.id - b.id)
    }

    @Emit('cancel')
    cancel(): void {
        // 初期化処理
        // currentPageは変更するとAPIリクエストが発火するため、モーダルを開いた際に初期化を行う
        this.searchType = 'name'
        this.keyword = ''
        this.resetSortState()
        this.selectedItems = []
        this.showAllChemicals = false
    }

    private sortChemicalMaster(column: 'id' | 'display_type' | 'display_name') {
        if (this.sortColumn === column) {
            this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
        } else {
            this.sortColumn = column
            this.sortOrder = 'asc'
        }
        this.refreshChemicalList()
    }

    // 農薬登録情報のリストを取得
    private async getChemicals() {
        const conditions = []
        conditions.push(`search_by_${this.searchType}=${this.keyword}`)
        conditions.push(`offset=${this.offset}`)
        conditions.push(`limit=${this.limit}`)
        conditions.push(`sort_column=${this.sortColumn}`)
        conditions.push(`sort_order=${this.sortOrder}`)
        const res = await this.$apiClient.doRequest('get', `/admin/chemicals?${conditions.join('&')}`) as ChemicalsResponse
        this.chemicals = toCamel(res) as Chemicals
    }

    private async getChemicalsByCropItemId() {
        const conditions = []
        conditions.push(`search_by_${this.searchType}=${this.keyword}`)
        conditions.push(`offset=${this.offset}`)
        conditions.push(`limit=${this.limit}`)
        conditions.push(`sort_column=${this.sortColumn}`)
        conditions.push(`sort_order=${this.sortOrder}`)
        const response = await this.$apiClient.doRequest('get',`/admin/chemicals/${this.cropItemId}?${conditions.join('&')}`) as ChemicalsResponse
        this.chemicals = toCamel(response) as Chemicals
    }

    @Watch('showAllChemicals')
    async onShowAllChemicalsChanged(newVal: boolean) {
        this.doPaginate(1);
        this.currentPage = 1;
    }

    // 初期表示、検索時にリストを再作成する
    private async refreshChemicalList() {
        this.isLoading = true
        try {
            // offset初期化
            this.offset = 0
            // 農薬リストの取得
            if (this.isFromShippingItems && this.cropItemId && this.showAllChemicals === false) {
                await this.getChemicalsByCropItemId()
            } else {
                await this.getChemicals()
            }
            // 総件数の再計算とページ選択状態の初期化
            this.calcTotalPages()
            this.currentPage = 1
        } finally {
            this.isLoading = false
        }
    }

    // ページング処理
    private async doPaginate(pageNo: number) {
        this.isLoading = true
        try {
            this.offset = (pageNo - 1) * this.limit
            if (this.showAllChemicals === false && this.isFromShippingItems && this.cropItemId) {
                await this.getChemicalsByCropItemId()
            } else {
                await this.getChemicals()
            }
            this.calcTotalPages();
        } finally {
            this.isLoading = false
        }
    }

    // 総ページ数の計算
    private calcTotalPages() {
        this.totalPages = Math.ceil( this.chemicals.totalCount / this.limit )
    }

}
